import { message } from 'antd';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { request } from '../utils/axios-utils';

export const fetchReportSkus = params => {
    return request({
        url: '/report/skus',
        method: 'GET',
        params,
    });
};

const fetchReportSummary = params => {
    return request({
        url: '/report/skus/summary',
        method: 'GET',
        params,
    });
};

export const fetchFmsDeepDive = params => {
    return request({
        url: '/report/skus/fms-deep-dive',
        method: 'GET',
        params,
    });
};

export const fetchReportSkusByIds = body => {
    return request({
        url: '/report/skus/by-ids',
        method: 'POST',
        data: body,
    });
};

export const useReportSummary = (params, enabled) => {
    return useQuery(['reportSkuSummary', params], () => fetchReportSummary(params), {
        enabled: !!enabled,
        refetchOnWindowFocus: false,
        onError: err => {
            const payload = err?.response?.data?.message;
            message.error(typeof payload === 'string' ? payload : 'Could not load summary');
        },
    });
};

export const useReportSkus = (params, enabled) => {
    return useQuery(['reportSkus', params], () => fetchReportSkus(params), {
        enabled: !!enabled,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        onError: err => {
            const payload = err?.response?.data?.message;
            message.error(typeof payload === 'string' ? payload : 'Could not load SKUs');
        },
    });
};

export const useReportSkusByIds = (body, enabled) => {
    return useQuery(['reportSkusByIds', body], () => fetchReportSkusByIds(body), {
        enabled: !!enabled,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        onError: err => {
            const payload = err?.response?.data?.message;
            message.error(typeof payload === 'string' ? payload : 'Could not load SKUs');
        },
    });
};

export const useFmsDeepDive = (params, enabled) => {
    return useQuery(['reportFmsDeepDive', params], () => fetchFmsDeepDive(params), {
        enabled: !!enabled,
        refetchOnWindowFocus: false,
        onError: err => {
            const payload = err?.response?.data?.message;
            message.error(typeof payload === 'string' ? payload : 'Could not load FMS deep dive');
        },
    });
};

const fetchReportConfig = companyCode => {
    return request({
        url: '/report/config',
        method: 'GET',
        params: { companyCode },
    });
};

const saveReportConfig = body => {
    return request({
        url: '/report/config',
        method: 'PUT',
        data: body,
    });
};

export const useReportConfig = (companyCode, enabled) => {
    return useQuery(['reportConfig', companyCode], () => fetchReportConfig(companyCode), {
        enabled: !!enabled && !!companyCode,
        refetchOnWindowFocus: false,
        select: res => res.data.data,
        onError: err => {
            const payload = err?.response?.data?.message;
            message.error(typeof payload === 'string' ? payload : 'Could not load report config');
        },
    });
};

export const useSaveReportConfig = () => {
    const queryClient = useQueryClient();
    return useMutation(saveReportConfig, {
        onSuccess: (_res, variables) => {
            queryClient.invalidateQueries(['reportConfig', variables.companyCode]);
            message.success('Report config saved');
        },
        onError: err => {
            const payload = err?.response?.data?.message;
            message.error(typeof payload === 'string' ? payload : 'Could not save report config');
        },
    });
};

const fetchCsvKeys = companyCode => {
    return request({
        url: '/report/config/csv-keys',
        method: 'GET',
        params: { companyCode },
    });
};

const fetchCsvValues = ({ companyCode, key }) => {
    return request({
        url: '/report/config/csv-values',
        method: 'GET',
        params: { companyCode, key },
    });
};

export const useCsvKeys = (companyCode, enabled) => {
    return useQuery(['reportCsvKeys', companyCode], () => fetchCsvKeys(companyCode), {
        enabled: !!enabled && !!companyCode,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        select: res => res.data.data.keys || [],
        onError: err => {
            const payload = err?.response?.data?.message;
            message.error(typeof payload === 'string' ? payload : 'Could not load csvData fields');
        },
    });
};

export const useCsvValues = (companyCode, key, enabled) => {
    return useQuery(['reportCsvValues', companyCode, key], () => fetchCsvValues({ companyCode, key }), {
        enabled: !!enabled && !!companyCode && !!key,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        select: res => res.data.data.values || [],
        onError: err => {
            const payload = err?.response?.data?.message;
            message.error(typeof payload === 'string' ? payload : 'Could not load csvData values');
        },
    });
};
